import { CinemaDetailsPageProps, EventShowtimePickerPropsFromBe } from '../types'
import {
    CompositeFilterComponent,
    CompositeFilterProps,
    DateShowtimePickerComponent,
    DateShowtimePickerPropsWithFilmIds,
    DateShowtimePickerPropsWithoutFilmIds,
    TabData,
    Tabs,
    TabsDisplayType,
} from '@vista/omnichannel-components-ui'

import React, { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import { compositeFilterProps } from '../components/filmList/props'
import { FilterType, getFilterByIdAndType, ShowtimeFilterKey } from '@vista/omnichannel-components-domain'
import { dateShowtimePickerProps } from '../components/dateShowtimePicker/props'
import { getQueryParameterByName, UrlFactory } from '../utils'
import { Film } from '@vista-digital/ocapi-types/v1'
import EventShowtimePickerComponent from '../components/eventShowtimePicker/EventShowtimePickerComponent'
import ContentAccordionComponent from '../components/contentAccordion/ContentAccordionComponent'
import { observer } from 'mobx-react'
import { refreshDateShowtimePickerGlider } from '../utils/refreshGlider'

type DateShowtimePickerType = {
    dateShowtimePickerProps: DateShowtimePickerPropsWithFilmIds | DateShowtimePickerPropsWithoutFilmIds
    filterId: string
}

const DateShowtimePickerWrapper: React.FC<DateShowtimePickerType> = observer(props => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const filter = getFilterByIdAndType(props.filterId, FilterType.Showtime)
    const selectedGenres = filter ? filter.sections.genre.options.filter(option => option.selected) : []
    const selectedAttributes = filter ? filter.sections.attribute.options.filter(option => option.selected) : []
    const selectedFilterCount = selectedAttributes.length + selectedGenres.length
    /**
     * On filter change.The number of showtimes (slide item) will change in the carousel/glider.
     * Glider js must recalculate the space given depending on the slide items
     * */

    useEffect(() => {
        if (wrapperRef.current) {
            refreshDateShowtimePickerGlider(wrapperRef.current)
        }
    }, [selectedFilterCount])
    return (
        <div ref={wrapperRef}>
            <DateShowtimePickerComponent {...props.dateShowtimePickerProps} />
        </div>
    )
})

export const CinemaDetailsPage: React.FC<CinemaDetailsPageProps> = props => {
    const { EventShowtimePicker, DateShowtimePicker, InformationPanel, MenuPanel } = props

    const tabItems = [
        { label: 'Films', value: 1 },
        { label: 'Events', value: 2 },
        { label: 'Information', value: 3 },
        { label: 'Menu', value: 4 },
    ]

    const [selectedTab, setSelectedTab] = useState<TabData>(tabItems[0])

    const handleOnTabSelected = (tab: TabData) => setSelectedTab(tab)

    const siteId = props.DateShowtimePicker.workflow.entityIds.siteId
    const filmId = props.DateShowtimePicker.workflow.entityIds.filmId || getQueryParameterByName('filmId')

    const filterId = 'filter-details-' + selectedTab.label + '-' + siteId

    const tabOneCls = cls({
        'opened-tab cinema-tab__films': selectedTab.value === 1,
        'closed-tab cinema-tab__films': selectedTab.value !== 1,
    })

    const tabTwoCls = cls({
        'opened-tab cinema-tab__events': selectedTab.value === 2,
        'closed-tab cinema-tab__events': selectedTab.value !== 2,
    })

    const tabThreeCls = cls({
        'opened-tab cinema-tab__info': selectedTab.value === 3,
        'closed-tab cinema-tab__info': selectedTab.value !== 3,
    })

    const tabFourCls = cls({
        'opened-tab menu-tab__info': selectedTab.value === 4,
        'closed-tab menu-tab__info': selectedTab.value !== 4,
    })

    const eventShowtimePickerProps: EventShowtimePickerPropsFromBe = {
        siteDropdownMode: false,
        ...EventShowtimePicker,
    }

    const initialCompositeFilterProps = {
        config: {
            filter: {
                filterId: filterId,
            },
        },
    }

    const compositeFilterPropsObj: CompositeFilterProps | any = {
        ...compositeFilterProps(initialCompositeFilterProps),
        filterId,
        config: {
            filterType: FilterType.Showtime,
            displayMode: compositeFilterProps(initialCompositeFilterProps).config.displayMode,
            filters: {
                [ShowtimeFilterKey.Attribute]: {
                    enabled: true,
                    index: 0,
                    isMultiselect: true,
                },
                [ShowtimeFilterKey.Event]: {
                    enabled: false,
                    index: 1,
                    isMultiselect: true,
                },
                [ShowtimeFilterKey.Genre]: {
                    enabled: true,
                    index: 2,
                    isMultiselect: true,
                },
            },
        },
    }

    console.log('compositeFilterPropsObj', compositeFilterPropsObj)

    const initialShowtimePickerProps = { ...dateShowtimePickerProps(DateShowtimePicker) }

    const url = new UrlFactory(DateShowtimePicker.workflow)

    const dateShowtimePickerPropsObj: DateShowtimePickerPropsWithFilmIds | DateShowtimePickerPropsWithoutFilmIds = {
        ...initialShowtimePickerProps,
        siteIds: [siteId],
        config: {
            ...initialShowtimePickerProps.config,
            filter: {
                filterId,
            },
            filmUrlFactory: (film: Film) => `${url.getFilmDetailsUrl(film)}?siteId=${siteId}`,
            datePicker: {
                ...initialShowtimePickerProps.config.datePicker,
                carousel: {
                    refreshOnUpdate: true,
                },
            },
        },
    }

    console.log('dateShowtimePickerPropsObj', dateShowtimePickerPropsObj)

    if (filmId) {
        dateShowtimePickerPropsObj.filmIds = [filmId]

        /*@ts-ignore*/ // Sorry about this, type changes if there are filmIds
        delete dateShowtimePickerPropsObj.config.allFilmsToggle
    }

    const showCompositeFilter = !(selectedTab.label === 'Information' || selectedTab.label === 'Menu')

    return (
        <>
            <Tabs
                centered={true}
                tabList={tabItems}
                onTabSelected={handleOnTabSelected}
                displayType={TabsDisplayType.Primary}
                selectedTab={selectedTab}
            />
            {showCompositeFilter && <CompositeFilterComponent {...compositeFilterPropsObj} />}

            <div className={tabOneCls}>
                <DateShowtimePickerWrapper dateShowtimePickerProps={dateShowtimePickerPropsObj} filterId={filterId} />
            </div>
            <div className={tabTwoCls}>
                <EventShowtimePickerComponent {...eventShowtimePickerProps} />
            </div>
            <div className={tabThreeCls}>
                <ContentAccordionComponent {...InformationPanel} />
            </div>
            <div className={tabFourCls}>
                <ContentAccordionComponent {...MenuPanel} />
            </div>
        </>
    )
}

export default CinemaDetailsPage
